import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import '../styles/Auth.css';

function Login() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email_or_username: '',
    password: '',
    remember: false,
  });
  const [error, setError] = useState(searchParams.get('error') || '');
  const [success] = useState(searchParams.get('success') || '');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(formData);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const apiBase = api.defaults.baseURL?.replace(/\/$/, '') || window.location.origin;
    window.location.href = `${apiBase}/auth/google/start`;
  };

  return (
    <div className="auth-page">
      {/* ── Left Sidebar ── */}
      <div className="auth-sidebar">
        <Link to="/" className="auth-sidebar__logo">
          Postrify <span className="auth-sidebar__dot" />
        </Link>
        <div className="auth-sidebar__body">
          <h2 className="auth-sidebar__headline">
            Content that <span>works</span>,<br />on autopilot.
          </h2>
          <p className="auth-sidebar__desc">
            Postrify turns your brand identity into a steady stream of
            AI-generated posts — with images, captions, and perfect hashtags.
          </p>
          <ul className="auth-sidebar__features">
            {['AI-generated images & captions', 'Multi-brand dashboard', 'Auto-schedule & publish', 'Facebook & Instagram sync'].map((f) => (
              <li className="auth-sidebar__feature" key={f}>
                <span className="auth-sidebar__feature-icon">✓</span> {f}
              </li>
            ))}
          </ul>
        </div>
        <p className="auth-sidebar__footer">© 2024 Postrify</p>
      </div>

      {/* ── Right Panel ── */}
      <div className="auth-panel">
        <div className="auth-form-wrap">
          <Link to="/" className="auth-form-wrap__back">← Back to home</Link>

          <h1 className="auth-form-wrap__title">Welcome back</h1>
          <p className="auth-form-wrap__sub">Sign in to your Postrify account</p>

          {error   && <div className="ps-alert ps-alert--error">{error}</div>}
          {success && <div className="ps-alert ps-alert--success">{success}</div>}

          {/* Google */}
          <button onClick={handleGoogleLogin} className="auth-google-btn" type="button">
            <span className="auth-google-btn__icon">G</span>
            Continue with Google
          </button>

          <div className="auth-divider">
            <div className="auth-divider__line" />
            <span className="auth-divider__text">or sign in with email</span>
            <div className="auth-divider__line" />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="ps-field">
              <label className="ps-label" htmlFor="email_or_username">Email or Username</label>
              <input className="ps-input" type="text" id="email_or_username" name="email_or_username"
                required placeholder="you@example.com or username" autoComplete="username"
                value={formData.email_or_username} onChange={handleChange} />
            </div>

            <div className="ps-field">
              <label className="ps-label" htmlFor="password">
                <span style={{ display:'flex', justifyContent:'space-between', width:'100%' }}>
                  Password
                  <Link to="/forgot-password" style={{ color:'#4f46e5', fontWeight:600, textTransform:'none', letterSpacing:0, fontSize:'12px' }}>
                    Forgot password?
                  </Link>
                </span>
              </label>
              <input className="ps-input" type="password" id="password" name="password"
                required placeholder="Your password" autoComplete="current-password"
                value={formData.password} onChange={handleChange} />
            </div>

            <div className="ps-field">
              <label className="ps-checkbox-row">
                <input type="checkbox" name="remember" checked={formData.remember} onChange={handleChange} />
                Remember me for 30 days
              </label>
            </div>

            <button type="submit" className="ps-btn ps-btn--primary ps-btn--full ps-btn--lg" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in →'}
            </button>
          </form>

          <p className="auth-bottom">
            Don't have an account? <Link to="/register">Create one free</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
